import { Avatar } from "antd";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { db } from "../../../config/firebase";
import { toast } from "react-toastify";
import { useMyContext } from "../../../context/MyContext";
import { api } from "../../../config/api";
import dayjs from "dayjs";
import { Download, FileText, Loader2, Paperclip, Send, X, Check } from "lucide-react";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../../../config/firebase";
import { AiOutlineUser } from "react-icons/ai";

const AskForModel = ({ id, quoteNumber, proposalId }) => {
  const { user, onlineUsers } = useMyContext();
  const chatBoxRef = useRef(null);
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const isSubmitting = useRef(false);

  const optionsArray = [
    "super admin",
    "managing admin",
    "lead admin",
    "editor",
  ];

  // const [data, setData] = useState({});
  const [formData, setFormData] = useState({
    user: optionsArray.includes(user?.role) ? "admin" : user.name,
    message: "",
  });

  const { data, refetch } = useQuery(
    ["quote", id],
    async () => {
      const userDocRef = doc(db, "quotes", id);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const quoteData = userDocSnap.data();
        return {
          ...quoteData,
          comments: quoteData.comments || []
        };
      } else {
        throw new Error("Quote data not found");
      }
    },
    {
      refetchInterval: 5000,
      refetchIntervalInBackground: true,
    }
  );

  // Render two overlapped ticks for the "read" state
  const DoubleCheck = ({ size = 14, colorClass = "text-sky-300" }) => {
    const offset = Math.round(size * 0.45);
    return (
      <span
        className="relative inline-block align-middle"
        style={{ width: size + offset, height: size }}
      >
        <Check size={size} className={`${colorClass}`} style={{ position: 'absolute', top: 0, left: 0 }} />
        <Check size={size} className={`${colorClass}`} style={{ position: 'absolute', top: 0, left: offset }} />
      </span>
    );
  };

  const mutation = useMutation(
    async (newComment) => {
      const userDocRef = doc(db, "quotes", id);
      const updatedComments = [...(data.comments || []), newComment];
      await updateDoc(userDocRef, { comments: updatedComments });
      return updatedComments;
    },
    {
      onSuccess: (updatedComments) => {
        queryClient.setQueryData(["quote", id], (oldData) => ({
          ...oldData,
          comments: updatedComments,
        }));
        // No immediate refetch; cache already updated optimistically
      },
    }
  );

  const getAttachmentMeta = useCallback((comment) => {
    if (!comment) return null;
    const url =
      comment.fileUrl ||
      comment.attachmentUrl ||
      comment?.attachment?.url ||
      null;
    if (!url) return null;
    return {
      url,
      fileName:
        comment.fileName ||
        comment.attachmentName ||
        comment?.attachment?.name ||
        "Attachment",
    };
  }, []);

  const getAttachmentType = useCallback((fileName = "") => {
    const extension = fileName.split(".").pop()?.toLowerCase() || "file";
    if (extension === "pdf") return { label: "PDF", tone: "bg-red-100 text-red-700" };
    if (extension === "csv") return { label: "CSV", tone: "bg-emerald-100 text-emerald-700" };
    if (extension === "xls" || extension === "xlsx") {
      return { label: "XLS", tone: "bg-green-100 text-green-700" };
    }
    return { label: extension.toUpperCase().slice(0, 4), tone: "bg-slate-100 text-slate-700" };
  }, []);

  const downloadAttachment = useCallback(async (url, fileName) => {
    if (!url) {
      toast.error("Attachment URL is missing");
      return;
    }

    try {
      const response = await fetch(
        `/api/tracking/download-attachment?url=${encodeURIComponent(
          url
        )}&filename=${encodeURIComponent(fileName || "attachment")}`
      );
      if (!response.ok) throw new Error("Failed to fetch attachment");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "attachment";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (_error) {
      toast.error("Unable to download attachment. Please try again.");
    }
  }, []);

  // const fetchData = async () => {
  //   try {
  //     const userDocRef = doc(db, "quotes", id);
  //     const userDocSnap = await getDoc(userDocRef);

  //     if (userDocSnap.exists()) {
  //       const data = userDocSnap.data();
  //       setData(data);
  //     } else {
  //       toast.error("Quote data not found");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching Quote data:", error);
  //   }
  // };

  // Smooth auto-scroll to latest message
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTo({
        top: chatBoxRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [data?.comments]);

  const adminRoles = useMemo(() => (
    ["super admin", "managing admin", "lead admin", "editor"]
  ), []);

  const isAdmin = adminRoles.includes(user?.role);

  const counterpartyName = useMemo(() => {
    if (!isAdmin) return "Admin";
    return data?.userName || data?.user?.name || "User";
  }, [isAdmin, data]);

  const isOnline = useMemo(() => {
    if (!onlineUsers) return false;
    if (!isAdmin) {
      // Current user is a client; check any online admin
      return onlineUsers.some((u) => adminRoles.includes(u.role) && u.isOnline);
    }
    // Current user is admin; check if quote owner is online
    if (!data?.userId) return false;
    return onlineUsers.some((u) => (u.userId === data.userId) && u.isOnline);
  }, [onlineUsers, isAdmin, data, adminRoles]);

  useEffect(() => {
    const markNotificationsAsRead = async () => {
      if (quoteNumber) {
        try {
          await api.put(`/notifications/quotes/${user.uid}`, {
            role: optionsArray.includes(user?.role) ? "admin" : user?.role,
            quoteNumber: quoteNumber,
          });
          queryClient.invalidateQueries(["notifications"]);
        } catch (error) {
          console.error("Error marking notifications as read:", error);
        }
      }
    };

    markNotificationsAsRead();
  }, []);

  // Helper to decide if a comment belongs to the current user/admin
  const isOwn = useCallback(
    (c) => {
      const sender = (c?.user || "").toLowerCase();
      const viewerIsAdmin = optionsArray.includes(user?.role);
      if (viewerIsAdmin) {
        return sender === "admin";
      } else {
        return sender !== "admin";
      }
    },
    [user?.role, optionsArray]
  );

  // Best-effort delivery/read acknowledgements stored back in Firestore
  const markDeliveryAndRead = useCallback(async () => {
    if (!Array.isArray(data?.comments) || data.comments.length === 0) return;
    try {
      let changed = false;
      const now = dayjs().toISOString();
      const updated = data.comments.map((c) => {
        if (isOwn(c)) return c; // Only ack incoming messages
        const next = { ...c };
        if (!next.status || next.status === 'sent') {
          next.status = 'delivered';
          next.deliveredAt = next.deliveredAt || now;
          changed = true;
        }
        // Mark as read only when the tab is visible (i.e., the user can see it)
        if ((document?.visibilityState === 'visible') && next.status !== 'read') {
          next.status = 'read';
          next.readAt = now;
          changed = true;
        }
        return next;
      });
      if (changed) {
        const userDocRef = doc(db, "quotes", id);
        await updateDoc(userDocRef, { comments: updated });
        // Optimistically update cache
        queryClient.setQueryData(["quote", id], (old) => ({ ...(old || {}), comments: updated }));
      }
    } catch (e) {
      // non-blocking; ignore
    }
  }, [data?.comments, id, isOwn, queryClient]);

  // Run acks when comments change and also on visibility/focus
  useEffect(() => {
    markDeliveryAndRead();
  }, [markDeliveryAndRead]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') markDeliveryAndRead();
    };
    const onFocus = () => markDeliveryAndRead();
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') window.addEventListener('focus', onFocus);
    return () => {
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      if (typeof window !== 'undefined') window.removeEventListener('focus', onFocus);
    };
  }, [markDeliveryAndRead]);

  // useEffect(() => {
  //   fetchData();
  // }, [id]);

  const handleAddData = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      if (isSubmitting.current) return;
      if (!formData.message.trim() && !file) {
        toast.error("Please add a message or attach a file.");
        return;
      }

      setLoading(true);
      isSubmitting.current = true;
      let fileUrl = null;
      let fileName = null;
      try {
        if (file) {
          const path = `quote_comment_attachments/${Date.now()}_${file.name}`;
          const fileRef = ref(storage, path);
          await uploadBytes(fileRef, file);
          fileUrl = await getDownloadURL(fileRef);
          fileName = file.name;
        }

        const clientId = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

        const messageData = {
          ...formData,
          date: dayjs().format("DD MMM YYYY hh:mm A"),
          clientId,
          ...(fileUrl ? { fileUrl, fileName } : {}),
        };

        setFormData((prevFormData) => ({ ...prevFormData, message: "" }));
        setFile(null);
        setPreviewUrl("");

        // Optimistic UI update: append immediately, then persist in background
        queryClient.setQueryData(["quote", id], (old) => ({
          ...(old || {}),
          comments: [ ...((old && old.comments) || []), messageData ],
        }));
        mutation.mutate(messageData);

        // Notification logic
        const link =
          data?.status === "pending" || data?.status?.includes("decline")
            ? `/quoteadmin?quote=${id}`
            : `/shipments?ship=${id}`;

        if (optionsArray.includes(user?.role)) {
          // Fire-and-forget; do not block UI
          api.post("/notifications", {
            userId: data?.userId,
            title: "New Comment",
            message: messageData?.message || `Attachment: ${fileName || "file"}`,
            linkText: "See Quote",
            link,
            quoteNumber: data?.quoteNumber,
          }).catch(() => {});
        } else {
          api.post("/notifications", {
            userId: null,
            title: "New Comment",
            message: messageData?.message || `Attachment: ${fileName || "file"}`,
            linkText: "See Quote",
            link,
            role: "admin",
            quoteNumber: data?.quoteNumber,
          }).catch(() => {});
        }
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error("Error updating status. Please try again later.");
      } finally {
        setLoading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setTimeout(() => { isSubmitting.current = false; }, 300);
      }
    },
    [formData, id, data, user, mutation, optionsArray, file]
  );

  return (
    <div className="flex flex-col h-[70vh] w-full">
      {/* Header */}
      <div className="w-full bg-cus-blue py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
          <div className="relative">
            <Avatar
              icon={<AiOutlineUser />}
              className="bg-white text-cus-blue"
              size={36}
            />
            <div className={`absolute -top-1 -right-1 size-3 rounded-full ${isOnline ? "bg-green-500" : "bg-red-500"}`} />
          </div>
          <div className="flex flex-col items-start text-white">
            <p className="text-base font-medium">{counterpartyName}</p>
            <p className="text-[10px]">{isOnline ? "Online" : "Offline"}</p>
          </div>
          </div>
        </div>
      </div>
      {/* Messages Area - grows to fill space */}
      <div
        ref={chatBoxRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0"
      >
        {Array.isArray(data?.comments) && data.comments.length > 0 ? (
          (() => {
            const seen = new Set();
            const uniq = data.comments.filter((c) => {
              const key = c?.clientId || `${c?.user}|${c?.message}|${c?.date}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            return uniq.map((item, i) => {
            const isOwnMessage = isOwn(item);
            const attachment = getAttachmentMeta(item);
            const derivedStatus = item?.status || 'sent';

            return (
            <div
              key={i}
              className={`flex ${isOwnMessage ? "justify-end" : "justify-start"}`}
            >
              <div className="max-w-[70%]">
                <div
                  className={`rounded-2xl px-4 py-3 text-sm shadow-sm ${
                    isOwnMessage
                      ? "bg-cus-blue text-white rounded-tr-none"
                      : "bg-gray-200 text-gray-900 rounded-tl-none"
                  }`}
                >
                  {item.message ? <p>{item.message}</p> : null}
                  {attachment ? (
                    <button
                      type="button"
                      onClick={() => downloadAttachment(attachment.url, attachment.fileName)}
                      className={`mt-2 w-full inline-flex items-center justify-between gap-2 text-xs rounded-lg px-2 py-1.5 border transition-all ${
                        isOwnMessage
                          ? "bg-white/15 border-white/20 text-white hover:bg-white/25"
                          : "bg-white border-gray-300 text-cus-blue hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="size-6 rounded-md bg-black/10 flex items-center justify-center shrink-0">
                          <FileText size={13} />
                        </div>
                        <span className="max-w-[180px] truncate">{attachment.fileName}</span>
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${getAttachmentType(attachment.fileName).tone}`}
                        >
                          {getAttachmentType(attachment.fileName).label}
                        </span>
                      </div>
                      <div className="size-6 rounded-full bg-black/10 flex items-center justify-center shrink-0">
                        <Download size={12} />
                      </div>
                    </button>
                  ) : null}
                </div>
                <div className={`mt-1 flex items-center gap-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                  <span className={`text-[11px] text-gray-500 ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                    {(() => {
                      const label = (typeof item?.user === 'string' && item.user.toLowerCase() === 'admin') ? 'Admin' : (item?.user || '');
                      return `${item.date} • ${label}`;
                    })()}
                  </span>
                  {isOwnMessage ? (
                    <span className="order-1 flex items-center text-[10px] opacity-80">
                      {derivedStatus === 'read' ? (
                        <DoubleCheck size={14} colorClass="text-sky-300" />
                      ) : derivedStatus === 'delivered' ? (
                        <DoubleCheck size={14} colorClass="text-gray-400" />
                      ) : (
                        <Check size={14} className="text-gray-400" />
                      )}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
            );
          });
        })()
        ) : (
          <div className="text-center text-gray-500 py-8">
            No comments yet. Start the conversation!
          </div>
        )}
      </div>

      {/* Input Bar - fixed at bottom */}
      <div className="border-t border-gray-200 bg-white px-4 py-3 shrink-0">
        <div className="flex items-center gap-3">
          <label htmlFor="comment-file" className="border rounded-lg p-2 cursor-pointer flex items-center justify-center">
            <Paperclip size={18} />
            <input
              id="comment-file"
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setFile(f);
                if (f.type.startsWith("image/")) {
                  const reader = new FileReader();
                  reader.onload = () => setPreviewUrl(reader.result);
                  reader.readAsDataURL(f);
                } else {
                  setPreviewUrl("");
                }
              }}
              accept=".png,.jpg,.jpeg,.pdf,.xlsx,.xls,.csv"
              ref={fileInputRef}
            />
          </label>
          <input
            placeholder="Type your message..."
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAddData();
              }
            }}
            value={formData.message}
            className="flex-1 px-4 py-3 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cus-blue text-sm"
            autoFocus
          />
          {file && (
            <div className="flex items-center gap-1 text-xs bg-gray-100 rounded px-2 py-1">
              <span className="max-w-[140px] truncate">{file.name}</span>
              <button
                type="button"
                className="hover:text-red-500"
                onClick={() => { setFile(null); setPreviewUrl(""); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                aria-label="Remove attachment"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <button
            onClick={handleAddData}
            disabled={!formData.message.trim() && !file || loading}
            className="btnBg  disabled:opacity-50 text-white p-3 rounded-lg transition-all shadow-md focus:ring-cus-blue"
          >
            {loading ? <Loader2 className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AskForModel;
